import { getCanonicalFnv1a64Hash } from "@fate-kingdom/shared";

import type { CardDefinition, CardType } from "./card-schema.js";
import { CardCatalogSchema, type CardCatalog } from "./catalog-validation.js";
import type {
  CardDefinitionId,
  ConditionDefinitionId,
  EffectDefinitionId,
  LandTypeId,
  MandateId,
  SemanticVersion,
} from "./primitives.js";
import type { ResourceBundle } from "./resources.js";

/** Canonicalization/hash algorithm version; catalog content versions are embedded in the input. */
export const GAMEPLAY_CATALOG_HASH_PREFIX = "gameplay-catalog-fnv1a64-v1" as const;
export const ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION = 2 as const;
export const ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V3 = 3 as const;
export const ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V4 = 4 as const;

export interface EngineResourcePool {
  readonly food: number;
  readonly production: number;
  readonly trade: number;
  readonly science: number;
  readonly culture: number;
}

export interface EngineCardBaseDefinition {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: CardType;
}

/** Frozen wire shape retained for historical Replay V2 setup compatibility. */
export interface EngineLandStageDefinitionV1 {
  readonly stage: 1 | 2 | 3;
  readonly maximumControl: number;
}

export interface EngineLandCardDefinitionV1 extends EngineCardBaseDefinition {
  readonly type: "land";
  readonly developmentId: CardDefinitionId;
  readonly stages: readonly [
    EngineLandStageDefinitionV1 & { readonly stage: 1 },
    EngineLandStageDefinitionV1 & { readonly stage: 2 },
    EngineLandStageDefinitionV1 & { readonly stage: 3 },
  ];
}

export interface EngineNonLandCardDefinitionV1 extends EngineCardBaseDefinition {
  readonly type: Exclude<CardType, "land">;
}

export type EngineCardDefinitionV1 = EngineLandCardDefinitionV1 | EngineNonLandCardDefinitionV1;

export interface EngineGameplayCatalogV1 {
  readonly schemaVersion: 1;
  readonly cardDataVersion: SemanticVersion;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV1[];
}

export interface EngineLandStageDefinition {
  readonly stage: 1 | 2 | 3;
  readonly maximumControl: number;
  readonly production: EngineResourcePool;
}

export interface EngineLandCardDefinition extends EngineCardBaseDefinition {
  readonly type: "land";
  readonly landTypeId: LandTypeId;
  readonly developmentId: CardDefinitionId;
  readonly stages: readonly [
    EngineLandStageDefinition & { readonly stage: 1 },
    EngineLandStageDefinition & { readonly stage: 2 },
    EngineLandStageDefinition & { readonly stage: 3 },
  ];
}

export interface EngineArmyCardDefinition extends EngineCardBaseDefinition {
  readonly type: "army";
  readonly armySize: number;
  readonly attack: number;
  readonly guard: number;
  readonly upkeep: EngineResourcePool;
}

export interface EngineOtherCardDefinition extends EngineCardBaseDefinition {
  readonly type: Exclude<CardType, "land" | "army">;
}

export type EngineCardDefinition =
  EngineLandCardDefinition | EngineArmyCardDefinition | EngineOtherCardDefinition;

export interface EngineGameplayCatalogV2 {
  readonly schemaVersion: typeof ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION;
  readonly cardDataVersion: SemanticVersion;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinition[];
}

export interface EngineCardBaseDefinitionV3 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: CardType;
  readonly cost: EngineResourcePool;
}

export interface EngineConditionReferenceV3 {
  readonly conditionId: ConditionDefinitionId;
}

export interface EngineEffectReferenceV3 {
  readonly effectId: EffectDefinitionId;
}

/**
 * Declarative Commander Mandate data is projected for compatibility only.
 * The rules engine does not evaluate Mandates until their dedicated slice.
 */
export interface EngineMandateReferenceV3 {
  readonly mandateId: MandateId;
  readonly name: string;
  readonly condition: EngineConditionReferenceV3;
  readonly effects: readonly EngineEffectReferenceV3[];
}

export interface EngineLandCardDefinitionV3 extends EngineCardBaseDefinitionV3 {
  readonly type: "land";
  readonly landTypeId: LandTypeId;
  readonly developmentId: CardDefinitionId;
  readonly stages: readonly [
    EngineLandStageDefinition & { readonly stage: 1 },
    EngineLandStageDefinition & { readonly stage: 2 },
    EngineLandStageDefinition & { readonly stage: 3 },
  ];
}

export interface EngineCommanderCardDefinitionV3 extends EngineCardBaseDefinitionV3 {
  readonly type: "commander";
  readonly attack: number;
  readonly guard: number;
  readonly armyCapacity: number;
  readonly mandates: readonly EngineMandateReferenceV3[];
}

export interface EngineArmyCardDefinitionV3 extends EngineCardBaseDefinitionV3 {
  readonly type: "army";
  readonly armySize: number;
  readonly attack: number;
  readonly guard: number;
  readonly upkeep: EngineResourcePool;
}

export interface EngineOtherCardDefinitionV3 extends EngineCardBaseDefinitionV3 {
  readonly type: Exclude<CardType, "land" | "commander" | "army">;
}

export type EngineCardDefinitionV3 =
  | EngineLandCardDefinitionV3
  | EngineCommanderCardDefinitionV3
  | EngineArmyCardDefinitionV3
  | EngineOtherCardDefinitionV3;

export interface EngineGameplayCatalogV3 {
  readonly schemaVersion: typeof ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V3;
  readonly cardDataVersion: SemanticVersion;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV3[];
}

export type EngineEventEffectV1 =
  | { readonly type: "draw-cards"; readonly count: number }
  | { readonly type: "gain-kingdom-resources"; readonly resources: EngineResourcePool };

export type EngineCommanderCardDefinitionV4 = Omit<EngineCommanderCardDefinitionV3, "armyCapacity">;

export interface EngineEventCardDefinitionV4 extends EngineCardBaseDefinitionV3 {
  readonly type: "event";
  readonly effects: readonly EngineEventEffectV1[];
}

export interface EngineOtherCardDefinitionV4 extends EngineCardBaseDefinitionV3 {
  readonly type: "policy" | "conspiracy";
}

export type EngineCardDefinitionV4 =
  | EngineLandCardDefinitionV3
  | EngineCommanderCardDefinitionV4
  | EngineArmyCardDefinitionV3
  | EngineEventCardDefinitionV4
  | EngineOtherCardDefinitionV4;

export interface EngineGameplayCatalogV4 {
  readonly schemaVersion: typeof ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V4;
  readonly cardDataVersion: SemanticVersion;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV4[];
}

export type EventEffectPrograms = Readonly<Record<EffectDefinitionId, EngineEventEffectV1>>;

export type EngineGameplayCatalog =
  | EngineGameplayCatalogV1
  | EngineGameplayCatalogV2
  | EngineGameplayCatalogV3
  | EngineGameplayCatalogV4;

function compareStableIds(left: { readonly id: string }, right: { readonly id: string }): number {
  if (left.id < right.id) return -1;
  if (left.id > right.id) return 1;
  return 0;
}

/**
 * Only top-level arrays in the catalog are set-like. Nested arrays retain
 * their validated order because Land Stages and effect order are semantic.
 */
function createCanonicalGameplayCatalog(catalog: CardCatalog): CardCatalog {
  return {
    ...catalog,
    cards: [...catalog.cards].sort(compareStableIds),
    effects: [...catalog.effects].sort(compareStableIds),
    conditions: [...catalog.conditions].sort(compareStableIds),
    triggers: [...catalog.triggers].sort(compareStableIds),
  };
}

function normalizeEngineResourcePool(resources: ResourceBundle): EngineResourcePool {
  return {
    food: resources.food ?? 0,
    production: resources.production ?? 0,
    trade: resources.trade ?? 0,
    science: resources.science ?? 0,
    culture: resources.culture ?? 0,
  };
}

function projectCardDefinition(card: CardDefinition): EngineCardDefinition {
  if (card.type === "land") {
    return {
      id: card.id,
      name: card.name,
      type: "land",
      landTypeId: card.landTypeId,
      developmentId: card.developmentId,
      stages: [
        {
          stage: 1,
          maximumControl: card.stages[0].maximumControl,
          production: normalizeEngineResourcePool(card.stages[0].production),
        },
        {
          stage: 2,
          maximumControl: card.stages[1].maximumControl,
          production: normalizeEngineResourcePool(card.stages[1].production),
        },
        {
          stage: 3,
          maximumControl: card.stages[2].maximumControl,
          production: normalizeEngineResourcePool(card.stages[2].production),
        },
      ],
    };
  }

  if (card.type === "army") {
    return {
      id: card.id,
      name: card.name,
      type: "army",
      armySize: card.stats.armySize,
      attack: card.stats.attack,
      guard: card.stats.guard,
      upkeep: normalizeEngineResourcePool(card.upkeep),
    };
  }

  return {
    id: card.id,
    name: card.name,
    type: card.type,
  };
}

function projectCardDefinitionV3(card: CardDefinition): EngineCardDefinitionV3 {
  const base = {
    id: card.id,
    name: card.name,
    type: card.type,
    cost: normalizeEngineResourcePool(card.cost),
  };

  if (card.type === "land") {
    return {
      ...base,
      type: "land",
      landTypeId: card.landTypeId,
      developmentId: card.developmentId,
      stages: [
        {
          stage: 1,
          maximumControl: card.stages[0].maximumControl,
          production: normalizeEngineResourcePool(card.stages[0].production),
        },
        {
          stage: 2,
          maximumControl: card.stages[1].maximumControl,
          production: normalizeEngineResourcePool(card.stages[1].production),
        },
        {
          stage: 3,
          maximumControl: card.stages[2].maximumControl,
          production: normalizeEngineResourcePool(card.stages[2].production),
        },
      ],
    };
  }

  if (card.type === "commander") {
    return {
      ...base,
      type: "commander",
      attack: card.stats.attack,
      guard: card.stats.guard,
      armyCapacity: card.stats.armyCapacity,
      mandates: card.mandates.map((mandate) => ({
        mandateId: mandate.mandateId,
        name: mandate.name,
        condition: { conditionId: mandate.condition.conditionId },
        effects: mandate.effects.map((effect) => ({ effectId: effect.effectId })),
      })),
    };
  }

  if (card.type === "army") {
    return {
      ...base,
      type: "army",
      armySize: card.stats.armySize,
      attack: card.stats.attack,
      guard: card.stats.guard,
      upkeep: normalizeEngineResourcePool(card.upkeep),
    };
  }

  return {
    id: card.id,
    name: card.name,
    type: card.type,
    cost: normalizeEngineResourcePool(card.cost),
  };
}

function cloneEventEffect(effect: EngineEventEffectV1): EngineEventEffectV1 {
  return effect.type === "gain-kingdom-resources"
    ? { ...effect, resources: { ...effect.resources } }
    : { ...effect };
}

function validateEventEffectProgram(effectId: string, effect: EngineEventEffectV1): void {
  if (effect.type === "draw-cards") {
    if (!Number.isSafeInteger(effect.count) || effect.count <= 0) {
      throw new RangeError(`Event effect '${effectId}' draw count must be a positive integer.`);
    }
    return;
  }
  for (const [key, value] of Object.entries(effect.resources)) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new RangeError(`Event effect '${effectId}' resource '${key}' must be non-negative.`);
    }
  }
}

function projectCardDefinitionV4(
  card: CardDefinition,
  effectPrograms: EventEffectPrograms,
): EngineCardDefinitionV4 {
  const projected = projectCardDefinitionV3(card);
  if (projected.type === "commander") {
    return {
      id: projected.id,
      name: projected.name,
      type: projected.type,
      cost: projected.cost,
      attack: projected.attack,
      guard: projected.guard,
      mandates: projected.mandates,
    };
  }
  if (card.type === "event") {
    if (card.resolution.destination !== "discard" || card.enhancedResolution !== undefined) {
      throw new RangeError(
        `Public-beta Event '${card.id}' must use deterministic base resolution to discard.`,
      );
    }
    const effects = card.resolution.effects.map(({ effectId }) => {
      const effect = effectPrograms[effectId];
      if (effect === undefined) {
        throw new RangeError(`Public-beta Event '${card.id}' has no program for '${effectId}'.`);
      }
      validateEventEffectProgram(effectId, effect);
      return cloneEventEffect(effect);
    });
    return {
      id: card.id,
      name: card.name,
      type: "event",
      cost: normalizeEngineResourcePool(card.cost),
      effects,
    };
  }
  if (projected.type === "land" || projected.type === "army") return projected;
  if (card.type !== "policy" && card.type !== "conspiracy") {
    throw new RangeError(`Unsupported public-beta projection for '${card.id}'.`);
  }
  return {
    id: card.id,
    name: card.name,
    type: card.type,
    cost: normalizeEngineResourcePool(card.cost),
  };
}

/** Hash every validated gameplay field and referenced registry definition. */
export function getGameplayCatalogHash(catalog: CardCatalog): string {
  const validatedCatalog = CardCatalogSchema.parse(catalog);
  return getCanonicalFnv1a64Hash(
    createCanonicalGameplayCatalog(validatedCatalog),
    GAMEPLAY_CATALOG_HASH_PREFIX,
  );
}

/**
 * Convert validated data into the smallest serializable view required by the
 * current engine. Presentation metadata is not accepted here.
 */
export function createEngineGameplayCatalog(catalog: CardCatalog): EngineGameplayCatalogV2 {
  const validatedCatalog = CardCatalogSchema.parse(catalog);
  const canonicalCatalog = createCanonicalGameplayCatalog(validatedCatalog);

  return {
    schemaVersion: ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION,
    cardDataVersion: canonicalCatalog.cardDataVersion,
    gameplayCatalogHash: getCanonicalFnv1a64Hash(canonicalCatalog, GAMEPLAY_CATALOG_HASH_PREFIX),
    cards: canonicalCatalog.cards.map(projectCardDefinition),
  };
}

/**
 * Project the universal printed cost and Commander gameplay fields required by
 * Commander deployment without changing the frozen schema-V2 constructor.
 */
export function createEngineGameplayCatalogV3(catalog: CardCatalog): EngineGameplayCatalogV3 {
  const validatedCatalog = CardCatalogSchema.parse(catalog);
  const canonicalCatalog = createCanonicalGameplayCatalog(validatedCatalog);

  return {
    schemaVersion: ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V3,
    cardDataVersion: canonicalCatalog.cardDataVersion,
    gameplayCatalogHash: getCanonicalFnv1a64Hash(canonicalCatalog, GAMEPLAY_CATALOG_HASH_PREFIX),
    cards: canonicalCatalog.cards.map(projectCardDefinitionV3),
  };
}

/**
 * Public-beta projection. Commander Capacity is intentionally omitted and
 * Event references are resolved through a closed declarative program registry.
 */
export function createEngineGameplayCatalogV4(
  catalog: CardCatalog,
  effectPrograms: EventEffectPrograms,
): EngineGameplayCatalogV4 {
  const validatedCatalog = CardCatalogSchema.parse(catalog);
  const canonicalCatalog = createCanonicalGameplayCatalog(validatedCatalog);
  const gameplayCatalogHash = getCanonicalFnv1a64Hash(
    { catalog: canonicalCatalog, eventEffectPrograms: effectPrograms },
    GAMEPLAY_CATALOG_HASH_PREFIX,
  );
  return {
    schemaVersion: ENGINE_GAMEPLAY_CATALOG_SCHEMA_VERSION_V4,
    cardDataVersion: canonicalCatalog.cardDataVersion,
    gameplayCatalogHash,
    cards: canonicalCatalog.cards.map((card) => projectCardDefinitionV4(card, effectPrograms)),
  };
}
